import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Row, Space } from 'antd';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { RiArrowLeftLine } from 'react-icons/ri';
import { useIntl } from 'react-intl';
import isObject from 'lodash/isObject';
import { useSelector } from 'react-redux';
import { MdOutlineUnpublished } from 'react-icons/md';
import Yup from '../../../../../vendor/yup';
import IntlMessages from '../../../../../layout/components/lang/IntlMessages';
import urlPageCategories from '../../../../../urls/urlPageCategories';
import urlPageCategoryDetails from '../../../../../urls/urlPageCategoryDetails';
import { Permissions } from '../../../../../const/permissions';
import { PrivateComponent } from '../../../../../components/HasRights/HasRights';
import { fieldTypeToValidationRule, TYPE_IMAGE, TYPE_OBJECT } from '../../../../Template builder/const/templates';
import useModifiedDynamicDataErrors from '../../../../../app/hooks/useModifiedDynamicDataErrors';
import isFieldRequired from '../../../utils/isFieldRequired';
import { makeValidationSchema } from '../../../forms/FormCreateCategory/FormCreateCategory.const';
import createFileFromURL from '../../../../../lib/createFileFromURL';
import handleResponse from '../../../../../lib/handleResponse';
import transformErrors from '../../../../../lib/transformErrors';
import BreadCrumbs from '../../../../../layout/components/breadcrumbs';
import ActionButton from '../../../../../layout/components/action-button';
import FormEditCategory from '../../../forms/FormEditCategory';
import {
  useGetCategoryQuery,
  useUpdateCategoryMutation,
  useUnpublishCategoryMutation,
} from '../../../api/categoriesApiSlice';



let validation = {};

const PageEditCategory = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const language = useSelector(({ customise: { language } }) => language);
  const { categoryId } = useParams();
  const [ errors, setFormErrors ] = useState({});
  const [ initialValues, setInitialValues ] = useState({ template_id: null, data: {}, target: {} });

  const { data: category = {}, isFetching: isCategoryFetching } = useGetCategoryQuery({
    categoryId,
    includes: '?include=dynamic_data,template,target',
  });
  const [ updateCategory, { isLoading: isUpdatingCategory } ] = useUpdateCategoryMutation();
  const [ unpublishCategory, { isLoading: isUnpublishCategoryLoading } ] = useUnpublishCategoryMutation();


  const modifiedErrors = useModifiedDynamicDataErrors(
    category?.template?.data.id,
    category?.template?.data,
    errors,
    language,
  );

  const getDynamicData = (initialValues, detailsData) => {
    const data = {};
    const dataSchema = {};

    detailsData?.template?.data?.fields?.data.filter((field) => field.type !== TYPE_OBJECT)
      .forEach(async (field) => {
        const categoriesField = detailsData?.dynamic_data?.data?.find((item) => item.template_field_id === field.id);

        if (isFieldRequired(field?.validations?.data || [])) {
          dataSchema[field.full_name] = Yup.object().shape({
            template_field_id: Yup.number().required(),
            ...fieldTypeToValidationRule[field.type],
          });
        }

        if (field.type === TYPE_IMAGE) {
          if (categoriesField?.value) {
            const fileName = categoriesField.value?.split('/').pop();
            const file = await createFileFromURL(categoriesField?.value, fileName);

            data[field.full_name] = {
              value: [ {
                uid: '-1',
                name: categoriesField?.value,
                thumbUrl: categoriesField?.value,
                status: 'done',
                originFileObj: file,
                url: categoriesField?.value,
              } ],
              template_field_id: field.id,
            };
          } else {
            data[field.full_name] = {
              value: [],
              template_field_id: field.id,
            };
          }
        } else {
          data[field.full_name] = {
            value: categoriesField?.value,
            template_field_id: field.id,
          };
        }

        setInitialValues((prevState) => ({ ...prevState, data }));
      });

    validation = makeValidationSchema({
      data: Yup.object().shape(dataSchema),
    });

    return data;
  };


  useEffect(() => {
    const initialValues = {
      title: category?.title,
      client_id: category?.client_id,
      description: category?.description,
      options: category?.options,
      is_published: false,
      updated_at: category?.updated_at,
      published_at: category?.published_at,
      template_id: category?.template?.data?.id,
      target: {
        min_version: category?.target?.data?.min_version ?? '',
        max_version: category?.target?.data?.max_version ?? '',
        platform: category?.target?.data?.platform,
        countries: [ ...category?.target?.data.countries ?? [] ].sort(),
      },
      data: {},
    };

    setInitialValues(initialValues);
    getDynamicData(initialValues, category);

  }, [ isCategoryFetching ]);

  const handleUnpublish = () => {
    unpublishCategory(categoryId)
      .unwrap()
      .then(() => handleResponse('success', intl, 'ui-general-unpublished-successfully'))
      .catch((error) => handleResponse('error', intl, 'ui-general-unpublish-failed', [], error));
  };

  const handleUpdateCategory = (values) => {
    updateCategory({ categoryId, values })
      .unwrap()
      .then((response) => handleResponse(
        'success',
        intl,
        'ui-general-updated-successfully',
        [ () => navigate(urlPageCategoryDetails({ categoryId: response?.data?.id })) ],
      ))
      .catch((error) => handleResponse(
        'error',
        intl,
        'ui-general-update-failed',
        [ () => {
          if (isObject(error?.data?.errors)) {
            setFormErrors(transformErrors(error?.data?.errors));
          }
        } ],
        error,
      ));
  };

  return (
    <>
      <Row
        gutter={[ 32, 32 ]}
        justify="space-between"
        className="hp-mb-32"
        align="middle"
      >
        <BreadCrumbs
          breadCrumbParent={
            <Link to={urlPageCategories()}>
              <IntlMessages id="categories-breadcrumbs" />
            </Link>
          }
          breadCrumbParent2={
            <Link to={urlPageCategoryDetails({ categoryId })}>
              {categoryId}
            </Link>
          }
          breadCrumbActive={<IntlMessages id='ui-general-edit' />}
        />
        <Col md={12} span={24}>
          <Row
            gutter={[ 8, 8 ]}
            justify="end"
            align="middle"
          >
            <PrivateComponent allowedPermissions={[ Permissions.CONTENT.CATEGORIES.UNPUBLISH ]}>
              {Boolean(category?.is_published) && (
                <Col>
                  <Space>
                    <ActionButton
                      loading={isUnpublishCategoryLoading}
                      title={intl.formatMessage({ id: 'ui-general-unpublish' })}
                      icon={<MdOutlineUnpublished className="hp-mr-8" size={18} />}
                      onClick={handleUnpublish}
                    />
                  </Space>
                </Col>
              )}
            </PrivateComponent>
            <Col>
              <Space>
                <Link to={urlPageCategories()}>
                  <Button
                    type="primary"
                    className="hp-mr-sm-8"
                    ghost="true"
                    icon={<RiArrowLeftLine className="hp-mr-8" size={18} />}
                  >
                    <IntlMessages id='ui-general-back-to-list' />
                  </Button>
                </Link>
              </Space>
            </Col>
          </Row>
        </Col>
      </Row>

      <Row gutter={[ 32, 32 ]}>
        <Col span={24}>
          <Card className="hp-border-color-black-40 hp-card-6">
            <FormEditCategory
              initialErrors={modifiedErrors}
              initialValues={initialValues}
              isSubmitting={isCategoryFetching || isUpdatingCategory || isUnpublishCategoryLoading}
              onSubmit={handleUpdateCategory}
              validationSchema={validation}
              templateFields={category?.template?.data?.fields?.data || []}
              templateOptions={[ {
                label: category?.template?.data?.title,
                value: category?.template?.data?.id,
              } ]}
            />
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default PageEditCategory;
